import { DEFAULT_CURRENCY, DEFAULT_LOCALE, type Locale } from '@arriba.digital/contracts';
import { toIsoDate } from './calendar';

/**
 * Formatting helpers.
 *
 * The API sends decimal columns as fixed-scale strings (`"24.0000"`) so that
 * no precision is lost in JSON. Those strings are never parsed into a float
 * for display arithmetic; they are formatted by string manipulation plus
 * `Intl`, which keeps the ledger exact end to end.
 *
 * Counts and aggregates, by contrast, arrive as plain numbers, so every
 * helper accepts both.
 */

/** Anything the formatters can take. */
export type NumericInput = string | number | null | undefined;

/**
 * The `Intl` locale behind an interface language.
 *
 * The app stores three short codes, but `Intl` needs a region to pick the
 * right conventions: plain `es` resolves to Spain and renders the peso as
 * "100,00 ARS", while `es-AR` gives the Argentine form the users expect,
 * `$ 1.234,56`. English and Russian are pinned the same way so a formatting
 * change never depends on which ICU the browser happens to ship.
 */
export function intlLocale(locale: Locale): string {
  switch (locale) {
    case 'es':
      return 'es-AR';
    case 'en':
      return 'en-US';
    case 'ru':
      return 'ru-RU';
    default:
      return locale;
  }
}

function toRawString(value: NumericInput): string {
  if (value === null || value === undefined) {
    return '0';
  }
  return typeof value === 'number' ? String(value) : value;
}

/** Trim trailing zeros from a fixed-scale decimal string: "24.0000" -> "24". */
export function trimDecimal(value: NumericInput): string {
  const raw = toRawString(value);
  if (raw === '') {
    return '0';
  }

  const negative = raw.startsWith('-');
  const body = negative ? raw.slice(1) : raw;
  const [whole, fraction = ''] = body.split('.');
  const trimmedFraction = fraction.replace(/0+$/, '');
  const result = trimmedFraction ? `${whole}.${trimmedFraction}` : whole || '0';
  return negative && result !== '0' ? `-${result}` : result;
}

/** The locale's decimal separator: `.` in English, `,` in Spanish. */
function decimalSeparator(locale: Locale): string {
  return (
    new Intl.NumberFormat(intlLocale(locale))
      .formatToParts(1.1)
      .find((part) => part.type === 'decimal')?.value ?? '.'
  );
}

/** Group the integer part with the locale's separators, keeping the fraction. */
export function formatDecimal(
  value: NumericInput,
  locale: Locale = DEFAULT_LOCALE,
  maxFractionDigits = 4,
): string {
  const trimmed = trimDecimal(value);
  const negative = trimmed.startsWith('-');
  const [whole, fraction = ''] = (negative ? trimmed.slice(1) : trimmed).split('.');

  // Counts can arrive as a plain number and exceed BigInt's comfortable range
  // only in theory; `BigInt` is used because it never silently rounds.
  const grouped = new Intl.NumberFormat(intlLocale(locale)).format(BigInt(whole || '0'));
  const cappedFraction = fraction.slice(0, maxFractionDigits);
  const result = cappedFraction
    ? `${grouped}${decimalSeparator(locale)}${cappedFraction}`
    : grouped;

  return negative && result !== '0' ? `-${result}` : result;
}

/** Quantities: up to 4 decimals, trailing zeros removed. */
export function formatQuantity(value: NumericInput, locale: Locale = DEFAULT_LOCALE): string {
  return formatDecimal(value, locale, 4);
}

/**
 * Money: exactly 2 decimals, grouped, with the currency symbol placed by
 * `Intl`. The default currency is the Argentine peso, the market the product
 * is built for; a tenant that trades in another currency passes it in.
 */
export function formatMoney(
  value: NumericInput,
  locale: Locale = DEFAULT_LOCALE,
  currency = DEFAULT_CURRENCY,
): string {
  const trimmed = trimDecimal(value);
  const negative = trimmed.startsWith('-');
  const [whole, fraction = ''] = (negative ? trimmed.slice(1) : trimmed).split('.');
  const cents = fraction.padEnd(2, '0').slice(0, 2);
  const grouped = new Intl.NumberFormat(intlLocale(locale)).format(BigInt(whole || '0'));
  const sign = negative && (whole !== '0' || cents !== '00') ? '-' : '';

  try {
    // Ask `Intl` where the symbol goes for this locale and currency, then
    // splice in the exact digits — `Intl` itself would round a string through
    // a float.
    const parts = new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).formatToParts(0);

    // The separator and the symbol position are locale-specific (`$ 1.234,56`
    // in Argentine Spanish, `$1,234.56` in English), so they are read from the
    // template rather than assumed.
    const symbol = parts.find((part) => part.type === 'currency')?.value ?? currency;
    const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value ?? '.';

    const template = parts
      .map((part) => {
        if (part.type === 'currency') return '{symbol}';
        if (part.type === 'integer' || part.type === 'group') return '{number}';
        if (part.type === 'decimal') return '{decimal}';
        if (part.type === 'fraction') return '{fraction}';
        if (part.type === 'minusSign') return '';
        return part.value;
      })
      .join('');

    return (
      sign +
      template
        .replace('{symbol}', symbol)
        .replace('{number}', grouped)
        .replace('{decimal}', decimalSeparator)
        .replace('{fraction}', cents)
    );
  } catch {
    // Unknown currency code: fall back to a plain suffix.
    return `${sign}${grouped}.${cents} ${currency}`;
  }
}

/** `2026-09-29T16:03:23.145Z` -> a short local date. */
export function formatDate(
  value: string | null | undefined,
  locale: Locale = DEFAULT_LOCALE,
): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(
  value: string | null | undefined,
  locale: Locale = DEFAULT_LOCALE,
): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Signed quantity, always showing the sign: "+5" / "−3". */
export function formatSignedQuantity(value: NumericInput, locale: Locale = DEFAULT_LOCALE): string {
  const trimmed = trimDecimal(value);
  const formatted = formatQuantity(trimmed, locale);
  return trimmed.startsWith('-') ? formatted : `+${formatted}`;
}

/** `YYYY-MM-DD` for an `<input type="date">` value or the API's date field. */
export function toDateInputValue(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  return toIsoDate(date);
}

/** Relative "3 minutes ago" label for the ledger and audit lists. */
export function formatRelative(
  value: string | null | undefined,
  locale: Locale = DEFAULT_LOCALE,
): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' });

  const thresholds: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.34],
    ['month', 12],
  ];

  let duration = diffSeconds;
  for (const [unit, limit] of thresholds) {
    if (Math.abs(duration) < limit) {
      return formatter.format(Math.round(duration), unit);
    }
    duration /= limit;
  }

  return formatter.format(Math.round(duration), 'year');
}
